import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { gsap } from 'gsap';

/**
 * Component that just displays a floating blob shape
 */
@Component({
  standalone: true,
  selector: 'ct-lib-floating-shape',
  templateUrl: './floating-shape.component.html',
  styleUrls: ['./floating-shape.component.scss'],
  imports: [],
})
export class FloatingShapeComponent implements OnInit, AfterViewInit {
  @ViewChild('path', {read: ElementRef}) path: ElementRef;
  @Input() scale = 1;
  @Input() accentColor: string;
  viewBox: string;

  

  ngOnInit(): void {
    this.viewBox = `0 0 ${1000 * this.scale} ${1000 * this.scale}`;
  }

  ngAfterViewInit() {
    this.createBlob({
      element: this.path.nativeElement,
      numPoints: 10,
      centerX: 500 * this.scale,
      centerY: 500 * this.scale,
      minRadius: 250 * this.scale,
      maxRadius: 325 * this.scale,
      minDuration: 5,
      maxDuration: 10,
    });
  }

  createBlob(options: any) {
    const points: Array<any> = [];
    const path: any = options.element;
    const slice: number = (Math.PI * 2) / options.numPoints;
    const startAngle: number = this.random(Math.PI * 2);

    const tl: any = gsap.timeline({
      onUpdate: () => {
        path.setAttribute('d', this.cardinal(points, true, 1));
      },
    });

    for (let i = 0; i < options.numPoints; i++) {
      const angle: number = startAngle + i * slice;
      const duration: number = this.random(
        options.minDuration,
        options.maxDuration
      );

      const point: any = {
        x: options.centerX + Math.cos(angle) * options.minRadius,
        y: options.centerY + Math.sin(angle) * options.minRadius,
      };

      const tween: any = gsap.to(point, {
        duration,
        x: options.centerX + Math.cos(angle) * options.maxRadius,
        y: options.centerY + Math.sin(angle) * options.maxRadius,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });

      tl.add(tween, -this.random(duration));
      points.push(point);
    }

    options.tl = tl;
    options.points = points;

    return options;
  }

  cardinal(
    data: Array<{ x: number; y: number }>,
    closed: boolean,
    tension?: number
  ) {
    if (data.length < 1) return 'M0 0';
    if (tension == null) tension = 1;

    const size = data.length - (closed ? 0 : 1);
    let path = 'M' + data[0].x + ' ' + data[0].y + ' C';

    for (let i = 0; i < size; i++) {
      let p0, p1, p2, p3;

      if (closed) {
        p0 = data[(i - 1 + size) % size];
        p1 = data[i];
        p2 = data[(i + 1) % size];
        p3 = data[(i + 2) % size];
      } else {
        p0 = i == 0 ? data[0] : data[i - 1];
        p1 = data[i];
        p2 = data[i + 1];
        p3 = i == size - 1 ? p2 : data[i + 2];
      }

      const x1: number = p1.x + ((p2.x - p0.x) / 6) * tension;
      const y1: number = p1.y + ((p2.y - p0.y) / 6) * tension;
      const x2: number = p2.x - ((p3.x - p1.x) / 6) * tension;
      const y2: number = p2.y - ((p3.y - p1.y) / 6) * tension;

      path +=
        ' ' + x1 + ' ' + y1 + ' ' + x2 + ' ' + y2 + ' ' + p2.x + ' ' + p2.y;
    }

    return closed ? path + 'z' : path;
  }

  random(min: number, max?: number): number {
    if (max == null) {
      max = min;
      min = 0;
    }
    if (min > max) {
      const tmp: number = min;
      min = max;
      max = tmp;
    }
    return min + (max - min) * Math.random();
  }

  get correctedAccentColor(): string {
    if (this.accentColor) {
      if (
        this.accentColor.startsWith('rgb') &&
        !this.accentColor.startsWith('rgba')
      ) {
        return (
          this.accentColor.substring(0, this.accentColor.length - 2) +
          ', 0.133333)'
        );
      } else if (
        this.accentColor.startsWith('#') &&
        this.accentColor.length <= 7
      ) {
        return this.accentColor + '22';
      } else {
        return this.accentColor;
      }
    }
    return '';
  }
}
